import { useState } from "react";
import { Controller } from "react-hook-form";
import type { Control, FieldValues, Path } from "react-hook-form";
import { IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import type { TextFieldProps } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import type { ReactNode } from "react";
import { FieldLabel } from "./FieldLabel";

interface Props<T extends FieldValues>
  extends Omit<TextFieldProps, "name" | "error" | "helperText"> {
  name: Path<T>;
  control: Control<T>;
  label: string;
  required?: boolean;
  helperText?: ReactNode;
  // Leading icon rendered inside the field (start adornment).
  startIcon?: ReactNode;
  // Dense rows: hide the stacked label (the label becomes the placeholder) and
  // suppress helper text so the field stays a single, fixed-height line.
  hideLabel?: boolean;
  // Cap the field width so it doesn't sprawl in a wide container. Still fills
  // 100% up to this cap (so it fills small flex cells in dense rows).
  maxWidth?: number | string;
  // Hard character cap (native maxLength on the input/textarea — blocks typing
  // and paste-over). Pair with the matching zod .max() and the server cap.
  maxLength?: number;
  // Show a live "n/maxLength" counter under the field. Requires maxLength and a
  // visible label (ignored in hideLabel dense rows).
  showCount?: boolean;
  // Fires with the new value AFTER the form field updates, on USER input only —
  // programmatic setValue/reset bypass the DOM event, so this won't fire on load
  // or pre-fill. Use for cross-field reactions (e.g. clear a dependent field).
  onValueChange?: (value: string) => void;
}

export function LabeledTextField<T extends FieldValues>(
  props: Props<T>,
): React.ReactElement {
  const {
    name,
    control,
    label,
    required,
    helperText,
    startIcon,
    hideLabel,
    maxWidth = 600,
    maxLength,
    showCount,
    onValueChange,
    type,
    slotProps,
    ...rest
  } = props;
  const labelHidden = hideLabel === true;

  // Built-in show/hide toggle for password fields.
  const isPassword = type === "password";
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const effectiveType = isPassword && showPassword ? "text" : type;

  // Callers may pass an object or callback form of `slotProps.input`; only the
  // object form can be merged with our adornments.
  const callerInput =
    slotProps?.input !== undefined && typeof slotProps.input === "object"
      ? slotProps.input
      : undefined;
  const callerHtmlInput =
    slotProps?.htmlInput !== undefined && typeof slotProps.htmlInput === "object"
      ? slotProps.htmlInput
      : undefined;

  const startAdornment =
    startIcon !== undefined ? (
      <InputAdornment position="start">{startIcon}</InputAdornment>
    ) : undefined;
  const endAdornment = isPassword ? (
    <InputAdornment position="end">
      <IconButton
        size="small"
        edge="end"
        aria-label={showPassword ? "Hide password" : "Show password"}
        onClick={() => {
          setShowPassword((prev) => !prev);
        }}
      >
        {showPassword ? (
          <VisibilityOffOutlinedIcon fontSize="small" />
        ) : (
          <VisibilityOutlinedIcon fontSize="small" />
        )}
      </IconButton>
    </InputAdornment>
  ) : undefined;

  return (
    <Stack sx={{ maxWidth, width: "100%" }}>
      {labelHidden ? null : (
        <FieldLabel label={label} required={required === true} htmlFor={name} />
      )}
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const rawValue: unknown = field.value;
          const valueLength = typeof rawValue === "string" ? rawValue.length : 0;
          const counterVisible =
            showCount === true && maxLength !== undefined && !labelHidden;
          const atLimit = maxLength !== undefined && valueLength >= maxLength;
          return (
            <>
              <TextField
                {...rest}
                {...field}
                value={field.value ?? ""}
                onChange={(event) => {
                  field.onChange(event);
                  onValueChange?.(event.target.value);
                }}
                id={name}
                type={effectiveType}
                fullWidth
                required={required === true}
                placeholder={rest.placeholder ?? (labelHidden ? label : undefined)}
                error={fieldState.error !== undefined}
                helperText={
                  counterVisible || labelHidden
                    ? undefined
                    : (fieldState.error?.message ?? helperText)
                }
                slotProps={{
                  ...slotProps,
                  ...(maxLength !== undefined
                    ? { htmlInput: { ...callerHtmlInput, maxLength } }
                    : {}),
                  input: {
                    ...callerInput,
                    ...(startAdornment !== undefined ? { startAdornment } : {}),
                    ...(endAdornment !== undefined ? { endAdornment } : {}),
                  },
                }}
              />
              {counterVisible ? (
                <Stack
                  direction="row"
                  sx={{ mt: 0.5, px: 0.25, gap: 1, alignItems: "baseline" }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      flex: 1,
                      color:
                        fieldState.error !== undefined
                          ? "error.main"
                          : "text.secondary",
                    }}
                  >
                    {fieldState.error?.message ?? helperText}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      flexShrink: 0,
                      color: atLimit ? "error.main" : "text.secondary",
                    }}
                  >
                    {valueLength}/{maxLength}
                  </Typography>
                </Stack>
              ) : null}
            </>
          );
        }}
      />
    </Stack>
  );
}
